import * as React from 'react'
import { FunctionComponent, ReactElement } from 'react'
import { DetailsRow, IColumn, IconButton, IDetailsRowProps } from '@fluentui/react'
import LegacyLocalDatePicker, { LocalDatePickerProps } from '../../../components/LegacyLocalDatePicker'
import { Formik, FormikConfig, useFormikContext } from 'formik'
import { RegistrationMutationDto, RegistrationOverviewDto } from '../../../config/registrationConfig'
import { RegistrationItemProps } from './RegistrationList'
import { Input, InputProps, makeStyles } from '@fluentui/react-components'
import FormikField from '../../Formik/FormikField'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'

export const useRegistrationRowStyles = makeStyles({
    rate: {
        width: '78px',
        minWidth: 'auto',
    },
    rateInput: {
        width: '78px',
    },
    description: {
        width: '100%',
    },
    dateInput: {
        width: '48px',
    },
})

export type ModifyRegistrationRowProps<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I = {}> = {
    listProps: RegistrationItemProps<I>
    rowProps: Omit<IDetailsRowProps, 'item'> & {
        item: Partial<D | RegistrationOverviewDto> & Pick<RegistrationOverviewDto, 'user'>
    }
    formikProps: FormikConfig<M>
}

export type ModifyRegistrationColumnProps<I = {}> = {
    column?: IColumn
} & RegistrationItemProps<I>

type Props<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I> = {
    columns: FunctionComponent<ModifyRegistrationColumnProps<I>>
} & ModifyRegistrationRowProps<D, M, I>

export function ModifyRegistrationColumns<M extends RegistrationMutationDto, I = {}>(
    props: ModifyRegistrationColumnProps<I>,
): ReactElement {
    const styles = useRegistrationRowStyles()
    const { values, handleChange, setFieldValue, submitForm } = useFormikContext<M>()
    const context = useFormikContext<M>()

    switch (props.column.key) {
        case 'date':
            return (
                <FormikField<LocalDatePickerProps>
                    context={context}
                    fieldName={'date'}
                    component={LegacyLocalDatePicker}
                    componentProps={{
                        isListColumn: true,
                        value: values.date,
                        onSelectDate: date => setFieldValue('date', date),
                        datePickerProps: {
                            placeholder: '-',
                            className: styles.dateInput,
                        },
                    }}
                />
            )
        case 'description':
            return (
                <FormikField<InputProps>
                    context={context}
                    fieldName={'description'}
                    component={Input}
                    componentProps={{
                        size: 'small',
                        value: values.description,
                        onChange: handleChange('description'),
                        className: styles.description,
                    }}
                />
            )
        case 'rate':
            return (
                <FormikField<MoneyInputProps>
                    context={context}
                    fieldName={'rate'}
                    component={MoneyInput}
                    componentProps={{
                        size: 'small',
                        currency: values.currency,
                        value: values.rate,
                        onCommit: (_, value: string) => {
                            setFieldValue('rate', value)
                        },
                        className: styles.rate,
                        input: { className: styles.rateInput },
                    }}
                />
            )
        case 'actions':
            return <IconButton iconProps={{ iconName: 'Save' }} onClick={submitForm} />
    }

    return null
}

export default function ModifyRegistrationRow<
    D extends RegistrationOverviewDto,
    M extends RegistrationMutationDto,
    I = {},
>(props: Props<D, M, I>): ReactElement {
    return (
        <Formik<M> {...props.formikProps}>
            <DetailsRow
                {...props.rowProps}
                compact
                onRenderItemColumn={(_item, _index?: number, column?: IColumn) => (
                    <props.columns {...props.listProps} column={column} />
                )}
            />
        </Formik>
    )
}
